import type { ProductPriceOption } from '@shared/types';

/**
 * Resolve the effective licensing period for a product given a requested period.
 *
 * Algorithm (per CR-PricingPeriodSelection):
 * 1. If `requestedPeriodMonths` is in the product's available periods → use it.
 * 2. Else: use the largest available period ≤ `requestedPeriodMonths`.
 * 3. Else: use the smallest available period (fallback when requested < minimum).
 *
 * Returns `undefined` if `prices` is empty or invalid.
 */
export function resolveEffectivePeriod(
  prices: ProductPriceOption[],
  requestedPeriodMonths: number,
): number | undefined {
  if (!prices || prices.length === 0) return undefined;

  const available = prices.map((p) => p.periodMonths).sort((a, b) => a - b);

  // 1. Exact match
  if (available.includes(requestedPeriodMonths)) {
    return requestedPeriodMonths;
  }

  // 2. Largest available ≤ requested
  const candidates = available.filter((p) => p <= requestedPeriodMonths);
  if (candidates.length > 0) {
    return candidates[candidates.length - 1]!;
  }

  // 3. Fallback: smallest available (requested < all options)
  return available[0]!;
}

/**
 * Find the price option matching the given effective period.
 * Returns `undefined` if no match.
 */
export function findPriceForPeriod(
  prices: ProductPriceOption[],
  periodMonths: number,
): ProductPriceOption | undefined {
  return prices.find((p) => p.periodMonths === periodMonths);
}

/**
 * Convenience: resolve effective period and return the matching price option.
 */
export function resolveEffectivePrice(
  prices: ProductPriceOption[],
  requestedPeriodMonths: number,
): ProductPriceOption | undefined {
  const effectivePeriod = resolveEffectivePeriod(prices, requestedPeriodMonths);
  if (effectivePeriod === undefined) return undefined;
  return findPriceForPeriod(prices, effectivePeriod);
}

/** Check whether a product has valid pricing (at least one price entry). */
export function hasValidPricing(prices: ProductPriceOption[] | undefined | null): boolean {
  return Array.isArray(prices) && prices.length > 0;
}
